import { domainHash } from '@pine/crypto'

/**
 * What makes an account a bot, as everybody else sees it.
 *
 * A bot is an ordinary Pine account run by a program (see packages/pine-bot):
 * same keys, same rooms, same end-to-end encryption. What it adds is a card —
 * what it does and which commands it answers — carried in two places:
 *
 *   - its live profile card in every room it is in (room.announceProfile),
 *     which is how a chat with it learns its commands without asking anybody;
 *   - the server, signed by the bot (/api/bots/card), so that somebody who
 *     has never met it sees "bot" in a search result before starting a chat.
 *
 * Nothing about a bot is trusted beyond that: a card can only describe the
 * account that signed it, and the worst a lying card can do is put a "bot"
 * label on its own author.
 */

export const BOT_ABOUT_MAX = 512
export const BOT_COMMANDS_MAX = 50
export const COMMAND_RE = /^[a-z0-9_]{1,32}$/

/** The card in the one shape both ends sign and store. Null when there is none. */
export function normalizeBotCard (card) {
  if (!card || typeof card !== 'object') return null
  const about = String(card.about || '').slice(0, BOT_ABOUT_MAX)
  const commands = []
  const seen = new Set()
  for (const raw of Array.isArray(card.commands) ? card.commands : []) {
    if (!raw) continue
    const command = String(raw.command || '').replace(/^\//, '').toLowerCase()
    if (!COMMAND_RE.test(command) || seen.has(command)) continue
    seen.add(command)
    commands.push({ command, description: String(raw.description || '').slice(0, 128) })
    if (commands.length >= BOT_COMMANDS_MAX) break
  }
  return { about, commands }
}

/**
 * What a bot signs to publish its card — or, with `card: null`, to withdraw
 * it. Must stay byte-identical to botCardSignable() in server/relay/lib/bots.js.
 */
export function botCardSignable ({ account, at, card }) {
  const normalized = card ? normalizeBotCard(card) : null
  return domainHash('pine/bots/card/v1', String(account), String(at),
    normalized ? JSON.stringify(normalized) : '')
}

/* ---- buttons under a message ---- */

export const BUTTON_ROWS_MAX = 8
export const BUTTONS_PER_ROW_MAX = 4

/**
 * Buttons a bot put under its message, cleaned for drawing.
 *
 * Two kinds. A button with `data` answers the bot: pressing it posts a
 * message carrying the data (see pressSignal), which the bot reads like any
 * other — so it works whenever the two meet, not only while both are online
 * at the same second. A button with `url` opens a link.
 */
export function normalizeButtons (rows) {
  if (!Array.isArray(rows)) return []
  const out = []
  for (const row of rows.slice(0, BUTTON_ROWS_MAX)) {
    if (!Array.isArray(row)) continue
    const clean = []
    for (const button of row.slice(0, BUTTONS_PER_ROW_MAX)) {
      if (!button || typeof button !== 'object') continue
      const text = String(button.text || '').trim().slice(0, 64)
      if (!text) continue
      const url = typeof button.url === 'string' && /^https?:\/\//i.test(button.url) ? button.url.slice(0, 512) : ''
      const data = url ? '' : String(button.data ?? text).slice(0, 64)
      clean.push(url ? { text, url } : { text, data })
    }
    if (clean.length) out.push(clean)
  }
  return out
}

/** What pressing a button posts, as the message's `extra`. */
export function pressExtra (messageId, data) {
  return { press: { to: String(messageId || ''), data: String(data || '').slice(0, 64) } }
}

/**
 * A command, if this text is one: "/start", "/weather Москва", or
 * "/start@weather_bot" in a group with several bots in it.
 */
export function parseCommand (text) {
  const match = String(text || '').match(/^\/([a-z0-9_]{1,32})(?:@([a-z0-9_]{3,32}))?(?:\s+([\s\S]*))?$/i)
  if (!match) return null
  return {
    command: match[1].toLowerCase(),
    mention: (match[2] || '').toLowerCase(),
    args: (match[3] || '').trim()
  }
}
